import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { getCourses } from "../../store/actions/councilActions/courseAction";
import {
  contactUs,
  getCenterApprovedTrades,
} from "../../store/actions/homeActions/homeActions";
import { errorAlert } from "../../utils/alert";
import StateCitySelector from "../misc/StateCitySelector";
import TradeSubtradeSelector from "../misc/TradeSubtradeSelector";

/*
  Admission enquiry, sent from a row of the training center list.

  It is the same enquiry the Contact Us page sends - enquiry_type 2, which is
  admission - so it lands in the council's Admission Enquiry list with
  everything else. The center whose row was clicked is written into the
  message, because that is the only thing that tells the council which center
  the visitor was looking at; nothing is assigned to the center here.
*/
const EMPTY = {
  name: "",
  mobile: "",
  email: "",
  address: "",
  state: "",
  city: "",
  message: "",
};

const EnquiryModal = ({ center, onHide }) => {
  const dispatch = useDispatch();
  const { tradeCourses } = useSelector((state) => state.admin);

  const [data, setData] = useState(EMPTY);
  // trade -> subtrade -> course cascade (only this centre's approved trades)
  const [sel, setSel] = useState({ trade: "", sub_trade: "" });
  const [course, setCourse] = useState("");
  const [approvedTrades, setApprovedTrades] = useState(null);
  const [busy, setBusy] = useState(false);

  // Load the courses this centre is approved for (as main trades).
  useEffect(() => {
    if (center?.id) {
      dispatch(getCenterApprovedTrades(center.id)).then((rows) =>
        setApprovedTrades(Array.isArray(rows) ? rows : [])
      );
    }
  }, [center?.id, dispatch]);

  // Load the courses whenever a sub-trade is chosen (and drop any old pick).
  useEffect(() => {
    setCourse("");
    if (sel.sub_trade) dispatch(getCourses(sel.sub_trade));
  }, [sel.sub_trade, dispatch]);

  // A fresh form every time a different center's button is used.
  useEffect(() => {
    setData(EMPTY);
    setSel({ trade: "", sub_trade: "" });
    setCourse("");
  }, [center?.id]);

  const set = (e) => setData({ ...data, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();

    if (!/^\d{10}$/.test(String(data.mobile).trim())) {
      return errorAlert("Please write a 10 digit mobile number.");
    }

    const where = [center?.center_name, center?.center_code]
      .filter(Boolean)
      .join(" - ");

    setBusy(true);
    const sent = await dispatch(
      contactUs({
        ...data,
        enquiry_type: "2",
        course,
        message: where
          ? `${data.message}\n\n[Enquiry for center: ${where}]`
          : data.message,
      })
    );
    setBusy(false);

    if (sent) {
      setData(EMPTY);
      setSel({ trade: "", sub_trade: "" });
      setCourse("");
      onHide();
    }
  };

  return (
    <Modal show={!!center} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          Admission Enquiry
          {center?.center_name ? ` - ${center.center_name}` : ""}
        </Modal.Title>
      </Modal.Header>
      <form onSubmit={submit}>
        <Modal.Body>
          <div class="row">
            <div class="col-md-6 mb-2">
              <label>Name *</label>
              <input
                type="text"
                class="form-control"
                name="name"
                value={data.name}
                onChange={set}
                required
              />
            </div>
            <div class="col-md-6 mb-2">
              <label>Mobile *</label>
              <input
                type="text"
                class="form-control"
                name="mobile"
                maxLength={10}
                value={data.mobile}
                onChange={set}
                required
              />
            </div>
            <div class="col-md-6 mb-2">
              <label>Email *</label>
              <input
                type="email"
                class="form-control"
                name="email"
                value={data.email}
                onChange={set}
                required
              />
            </div>
            {/* Only the courses this centre is approved for: trade -> subtrade
                -> course, so the visitor never scrolls a 1000-course list. */}
            <div class="col-md-12">
              <div class="row">
                <TradeSubtradeSelector
                  data={sel}
                  setData={setSel}
                  approvedTrades={approvedTrades}
                />
                {sel.sub_trade && (
                  <div class="col-md-4">
                    <div class="boxlabel">
                      <p>Course Name</p>
                      <select
                        class="form-control"
                        value={course}
                        onChange={(e) => setCourse(e.target.value)}
                      >
                        <option value="">Select Course</option>
                        {(tradeCourses || []).map((c) => (
                          <option value={c.id} key={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <StateCitySelector
              user_city={data.city}
              user_state={data.state}
              set_user_state={(value) => setData({ ...data, state: value })}
              set_user_city={(value) => setData({ ...data, city: value })}
            />

            <div class="col-md-12 mb-2">
              <label>Address *</label>
              <input
                type="text"
                class="form-control"
                name="address"
                value={data.address}
                onChange={set}
                required
              />
            </div>
            <div class="col-md-12 mb-2">
              <label>Message *</label>
              <textarea
                class="form-control"
                rows={3}
                name="message"
                value={data.message}
                onChange={set}
                required
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" class="btn btn-secondary" onClick={onHide}>
            Cancel
          </button>
          <button type="submit" class="btn btn-danger" disabled={busy}>
            {busy ? "Sending..." : "Send Enquiry"}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default EnquiryModal;
